import {
  collection, getDocs, getDoc, addDoc, doc, updateDoc, deleteDoc,
  query, orderBy, limit, startAfter, Timestamp
} from "firebase/firestore";
import { db, storage } from "../config/firebase";
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import DOMPurify from 'dompurify';
import categoryImageMap from "../config/categoryImageMap.json";

// Article functions and helpers
const ARTICLES_CACHE_KEY = "articles_cache";

const CP1252_CONTROL_REPLACEMENTS = {
  "\u0091": "'",
  "\u0092": "'",
  "\u0093": '"',
  "\u0094": '"',
  "\u0096": "-",
  "\u0097": "-",
  "\u0085": "...",
  "\u00A0": " ",
};

const stripControlChars = (value) =>
  Array.from(value).filter((char) => {
    const code = char.charCodeAt(0);
    return code > 31 && code !== 127;
  }).join("");

const stripC1Controls = (value) =>
  Array.from(value).filter((char) => {
    const code = char.charCodeAt(0);
    return code < 128 || code > 159;
  }).join("");

const normalizeTextEncoding = (value) => {
  if (typeof value !== "string") return value;
  return value
    .replace(/\\'/g, "'")
    .replace(/\\"/g, '"')
    .replace(/\\\//g, "/")
    .replace(/[\u0085\u0091\u0092\u0093\u0094\u0096\u0097\u00A0]/g, (match) => CP1252_CONTROL_REPLACEMENTS[match] || match)
    .replace(/\r?\n|\r/g, " ")
    .split(" ")
    .filter(Boolean)
    .join(" ")
    .trim();
};

const normalizeAndStripControls = (value) => {
  if (typeof value !== "string") return "";
  return stripC1Controls(stripControlChars(normalizeTextEncoding(value))).trim();
};

const fullyDecodeUrl = (value, maxRounds = 5) => {
  let current = value;
  for (let round = 0; round < maxRounds; round += 1) {
    try {
      const decoded = decodeURIComponent(current);
      if (decoded === current) break;
      current = decoded;
    } catch {
      break;
    }
  }
  return current;
};

const sanitizeExternalUrl = (rawUrl) => {
  if (typeof rawUrl !== "string") return "";

  const normalized = normalizeAndStripControls(
    fullyDecodeUrl(normalizeAndStripControls(rawUrl).trim())
  );
  const compact = normalized.replace(/\s+/g, "");

  if (!compact) return "";

  const withProtocol = /^https?:\/\//i.test(compact)
    ? compact
    : `https://${compact.replace(/^\/+/, "")}`;

  try {
    const parsed = new URL(withProtocol);
    return parsed.toString();
  } catch {
    return "";
  }
};

const resolvePublicAssetUrl = (assetPath) => {
  const cleaned = String(assetPath || "").trim();
  if (!cleaned) return "";

  // Keep absolute and data URLs untouched.
  if (/^(https?:)?\/\//i.test(cleaned) || cleaned.startsWith("data:")) {
    return cleaned;
  }

  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const relative = cleaned.startsWith("/") ? cleaned : `/${cleaned}`;
  return `${base}${relative}`;
};

const getCategoryImageFallback = (category) => {
  const normalizedCategory = normalizeTextEncoding(category || "");
  if (normalizedCategory && categoryImageMap[normalizedCategory]) {
    return resolvePublicAssetUrl(categoryImageMap[normalizedCategory]);
  }
  return "";
};

const resolveArticleTitle = (article) =>
  normalizeTextEncoding(article?.title || article?.Title || article?.headline || "");

const normalizeArticle = (article) => ({
  ...article,
  title: resolveArticleTitle(article),
  description: normalizeTextEncoding(article.description || ""),
  excerpt: normalizeTextEncoding(article.excerpt || ""),
  source: normalizeTextEncoding(article.source || ""),
  category: normalizeTextEncoding(article.category || ""),
  image: article.image || getCategoryImageFallback(article.category),
  sourceUrl: sanitizeExternalUrl(article.sourceUrl || ""),
});

export const loadArticles = async (useCache = true, pageSize = 10, lastVisible = null) => {
  try {
    let articlesData = [];
    if (useCache) {
      try {
        const cachedData = localStorage.getItem(ARTICLES_CACHE_KEY);
        if (cachedData) {
          const parsedData = JSON.parse(cachedData);
          articlesData = parsedData.articlesData;
          const cacheTimestamp = parsedData.timestamp;

          if (isCacheValid(cacheTimestamp)) {
            articlesData = articlesData.map(article => normalizeArticle({
              ...article,
              date: new Date(article.date)
            }));
            return articlesData;
          }
        }
      } catch {
        // localStorage unavailable (iOS Safari private mode / quota exceeded)
      }
    }

    const shouldLimit = Number.isFinite(pageSize) && Number(pageSize) > 0;

    let articlesQuery = shouldLimit
      ? query(
          collection(db, "articles"),
          orderBy("date", "desc"),
          limit(pageSize)
        )
      : query(
          collection(db, "articles"),
          orderBy("date", "desc")
        );

    if (lastVisible && shouldLimit) {
      articlesQuery = query(
        collection(db, "articles"),
        orderBy("date", "desc"),
        startAfter(lastVisible),
        limit(pageSize)
      );
    }

    const articlesSnapshot = await getDocs(articlesQuery);
    const newArticlesData = articlesSnapshot.docs.map(doc => {
      const data = doc.data();
      return normalizeArticle({
        id: doc.id,
        title: data.title || data.Title || data.headline,
        category: data.category,
        image: data.image,
        date: data.date.toDate(),
        description: data.description,
        excerpt: data.excerpt,
        source: data.source,
        sourceArticleId: data.sourceArticleId,
        uniqueKey: data.uniqueKey,
        isExternal: !!data.isExternal,
        contentSource: data.contentSource,
        sourceUrl: data.sourceUrl
      });
    });

    const uniqueArticlesData = [];
    const articleIds = new Set();
    [...articlesData, ...newArticlesData].forEach(article => {
      if (!articleIds.has(article.id)) {
        uniqueArticlesData.push(article);
        articleIds.add(article.id);
      }
    });

    try {
      localStorage.setItem(ARTICLES_CACHE_KEY, JSON.stringify({ articlesData: uniqueArticlesData, timestamp: new Date().getTime() }));
    } catch {
      // localStorage unavailable (iOS Safari private mode / quota exceeded)
    }

    return uniqueArticlesData;
    
  } catch (error) {
    console.error('Error loading articles:', error);
    throw error;
  }
};

export const loadArticle = async (articleId) => {
  try {
    try {
      const cachedData = localStorage.getItem(ARTICLES_CACHE_KEY);
      if (cachedData) {
        const { articlesData } = JSON.parse(cachedData);
        const articleData = articlesData.find(article => article.id === articleId);
        if (articleData) {
          return normalizeArticle({ ...articleData, date: new Date(articleData.date) });
        }
      }
    } catch {
      // localStorage unavailable (iOS Safari private mode / quota exceeded)
    }

    const articleDoc = await getDoc(doc(db, "articles", articleId));
    if (articleDoc.exists()) {
      const articleData = articleDoc.data();
      articleData.title = articleData.title || articleData.Title || articleData.headline || "";
      articleData.date = articleData.date.toDate();
      try {
        const cachedData = localStorage.getItem(ARTICLES_CACHE_KEY);
        let articlesData = [];
        if (cachedData) {
          const parsedData = JSON.parse(cachedData);
          articlesData = parsedData.articlesData;
        }
        articlesData = articlesData.filter(article => article.id !== articleId);
        articlesData.push({ id: articleId, ...articleData });
        localStorage.setItem(ARTICLES_CACHE_KEY, JSON.stringify({ articlesData, timestamp: new Date().getTime() }));
      } catch {
        // localStorage unavailable (iOS Safari private mode / quota exceeded)
      }
      return normalizeArticle({
        id: articleId,
        ...articleData,
        isExternal: !!articleData.isExternal,
      });
    } else {
      throw new Error(`Article with ID ${articleId} not found`);
    }
  } catch (error) {
    console.error('Error loading article:', error);
    throw error;
  }
};

const uploadArticleImage = async (image) => {
  const imageRef = ref(storage, `articles/${image.name}`);
  await uploadBytes(imageRef, image);
  const imageUrl = await getDownloadURL(imageRef);
  return imageUrl;
};

function cleanArticleData(articleData) {
  const articleDate = Timestamp.fromDate(new Date(articleData.date));
  const descriptionSingleLine = articleData.description.replace(/(\r\n|\n|\r)/gm, ' ');
  const sanitizedDescription = DOMPurify.sanitize(descriptionSingleLine);
  return { sanitizedDescription, articleDate };
}

export const addArticle = async (articleData) => {
  let imageUrl = null;
  if (articleData.image) {
    imageUrl = await uploadArticleImage(articleData.image);
  }
  const { sanitizedDescription, articleDate } = cleanArticleData(articleData);
  await addDoc(collection(db, 'articles'), {
    title: articleData.title,
    category: articleData.category,
    description: sanitizedDescription,
    date: articleDate,
    image: imageUrl,
  });
  emptyArticlesCache();
};

export const updateArticle = async (articleData) => {
  let imageUrl = articleData.image;
  if (articleData.image && articleData.image instanceof File) {
    imageUrl = await uploadArticleImage(articleData.image);
  }
  const { sanitizedDescription, articleDate } = cleanArticleData(articleData);
  const articleDocRef = doc(db, 'articles', articleData.id);
  await updateDoc(articleDocRef, {
    title: articleData.title,
    category: articleData.category,
    description: sanitizedDescription,
    date: articleDate,
    image: imageUrl,
  });
  emptyArticlesCache();
};

export const deleteArticle = async (articleId) => {
  try {
    if (!articleId) {
      throw new Error('Article ID is required');
    }
    await deleteDoc(doc(db, 'articles', articleId));
    emptyArticlesCache();
    return { success: true };
  } catch (error) {
    console.error('Error deleting article:', error);
    return { success: false, error };
  }
};

export const emptyArticlesCache = () => {
  try {
    localStorage.removeItem(ARTICLES_CACHE_KEY);
  } catch {
    // localStorage unavailable (iOS Safari private mode / quota exceeded)
  }
};

const isCacheValid = (timestamp) => {
  const now = new Date().getTime();
  const CACHE_EXPIRY = 2 * 60 * 60 * 1000;
  return now - timestamp < CACHE_EXPIRY;
};
